import { useEffect, useState } from 'react';
import type { NightOutcome, NightRun, Profile } from '../../../campaign';
import { t } from '../../../i18n';
import { sounds } from '../../../app/sound';
import { formatScore } from '../ScoreBurst';
import { BrokerHand } from './Broker';
import { NightIcon } from './NightIcon';
import { nightLabel, tableKindName } from './runText';

/**
 * A table won (docs/LONG_NIGHT_ART_DIRECTION.md §11.8): Balatro's cash out, as a pawnbroker's
 * receipt. A slip of violet glass slides in under his hand and its lines are written one per beat,
 * each dropping its tokens onto the pile beneath. Thrift shows the cards left in hand as card
 * backs; interest shows the tokens that earned it. Leaving tears it off.
 */
export function Receipt({
  profile,
  run,
  outcome,
  reduceMotion,
  onContinue,
}: {
  profile: Profile;
  run: NightRun;
  outcome: NightOutcome;
  reduceMotion: boolean;
  onContinue: () => void;
}) {
  const b = outcome.brass;
  const gained = b.table + b.thrift + b.interest + b.items;
  // The brass that earned the interest: what there was before the table paid out.
  const before = Math.max(0, run.brass - gained);
  const lines = [
    { key: 'table', label: t('receipt.table'), n: b.table },
    {
      key: 'thrift',
      label: t('receipt.thrift'),
      n: b.thrift,
      backs: Math.min(6, b.thrift),
    },
    {
      key: 'interest',
      label: t('receipt.interest', { n: before }),
      n: b.interest,
      tokens: Math.min(8, b.interest),
    },
    { key: 'items', label: t('receipt.items'), n: b.items },
    { key: 'match', label: t('receipt.match'), n: b.match },
  ].filter((l) => l.n !== 0);
  const total = lines.reduce((s, l) => s + l.n, 0);
  const [written, setWritten] = useState(reduceMotion ? lines.length + 1 : 0);
  const [tearing, setTearing] = useState(false);

  useEffect(() => {
    if (reduceMotion) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    // The slip slides in on a Glide, then a line a beat.
    const start = 900;
    lines.forEach((_, i) =>
      timers.push(
        setTimeout(
          () => {
            setWritten(i + 1);
            sounds.play('nib');
            sounds.tallyBeat(i, 'brass');
          },
          start + i * 420,
        ),
      ),
    );
    timers.push(
      setTimeout(
        () => {
          setWritten(lines.length + 1);
          sounds.play('thud');
        },
        start + lines.length * 420 + 200,
      ),
    );
    return () => timers.forEach(clearTimeout);
    // The lines are fixed for this receipt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion]);

  const tokens = lines.slice(0, written).reduce((s, l) => s + Math.max(0, l.n), 0);
  const next =
    run.phase === 'spoils'
      ? t('receipt.toSpoils')
      : run.phase === 'counter'
        ? t('receipt.toCounter')
        : t('action.next');
  const names = (ids: readonly string[]) =>
    ids.map((id) => profile.pawns[id]?.name ?? id).join(', ');

  return (
    <div className="ln-screen rc-scene has-dock">
      <BrokerHand pose={tearing ? 'away' : 'reach'} at={0.5} to={0.06} still={reduceMotion} />
      <div className={`rc-slip-glow ${tearing ? 'is-torn' : ''}`}>
        <div className="rc-slip">
          <p className="rc-head">
            {tableKindName(outcome.kind)} · {nightLabel(outcome.night)}
          </p>
          <p className="rc-score">
            {t('receipt.score', {
              you: formatScore(outcome.scores.S),
              them: formatScore(outcome.scores.N),
            })}
          </p>
          <ol className="rc-lines">
            {lines.map((l, i) => (
              <li key={l.key} className={i < written ? 'is-written' : ''}>
                <span className="rc-label">
                  {l.label}
                  {l.backs ? (
                    <span className="rc-backs" aria-hidden="true">
                      {Array.from({ length: l.backs }, (_, j) => (
                        <i key={j} />
                      ))}
                    </span>
                  ) : null}
                  {l.tokens ? (
                    <span className="rc-tokens" aria-hidden="true">
                      {Array.from({ length: l.tokens }, (_, j) => (
                        <i key={j} />
                      ))}
                    </span>
                  ) : null}
                </span>
                <span className="rc-dots" aria-hidden="true" />
                <span className="rc-n">
                  {l.n < 0 ? '−' : ''}¤{Math.abs(l.n)}
                </span>
              </li>
            ))}
            <li className={`rc-total ${written > lines.length ? 'is-written' : ''}`}>
              <span className="rc-label">{t('receipt.total')}</span>
              <span className="rc-dots" aria-hidden="true" />
              <span className="rc-n">¤{total}</span>
            </li>
          </ol>
          {/* After a High Table the Spoils show these in their own beats. */}
          {run.phase !== 'spoils' && outcome.reclaimed.length > 0 && (
            <p className="rc-note">
              {t('campaign.reclaimed', { names: names(outcome.reclaimed) })}
            </p>
          )}
          {run.phase !== 'spoils' && outcome.recruited.length > 0 && (
            <p className="rc-note">
              {t('campaign.recruited', { names: names(outcome.recruited) })}
            </p>
          )}
        </div>
      </div>
      {/* The tokens dropped so far, and what you hold now. */}
      <div className="rc-pan" aria-label={t('receipt.nowHave', { n: run.brass })}>
        <span className="rc-pan-tokens">
          {Array.from({ length: Math.min(14, tokens) }, (_, i) => (
            <i key={i} style={{ animationDelay: `${(i % 4) * 40}ms` }} />
          ))}
        </span>
        <span className="rc-have">{t('receipt.nowHave', { n: run.brass })}</span>
      </div>
      <div className="ln-dock">
        <button
          type="button"
          className="btn btn-primary btn-large ln-dock-main"
          autoFocus
          onClick={() => {
            if (reduceMotion) return onContinue();
            // The receipt is torn off and kept for the run's summary.
            sounds.play('tear');
            setTearing(true);
            setTimeout(onContinue, 420);
          }}
        >
          {next}
          <NightIcon name="arrow-right" size={18} />
        </button>
      </div>
    </div>
  );
}
